import { hybridCache } from './hybridCache';
import { createLogger } from './logger';

const logger = createLogger({ module: 'SmartCacheManager', level: 'warn' });

interface CacheDependency {
  key: string;
  dependsOn: string[];
  refreshFn?: () => Promise<unknown>;
}

export class SmartCacheManager {
  private dependencies = new Map<string, CacheDependency>();
  private refreshQueue = new Set<string>();
  private refreshInterval: ReturnType<typeof setTimeout> | null = null;
  
  constructor() {
    logger.info('Smart cache manager initialized');
    this.startBackgroundRefresh();
  }
  
  registerDependency(dependency: CacheDependency): void {
    this.dependencies.set(dependency.key, dependency);
    logger.debug('Registered cache dependency', { 
      key: dependency.key, 
      dependsOn: dependency.dependsOn 
    });
  }
  
  async invalidateWithDependencies(
    changedKey: string, 
    userId: string
  ): Promise<void> {
    const toInvalidate = new Set<string>([changedKey]);
    
    // Find all dependent keys
    for (const [key, dep] of this.dependencies) {
      if (dep.dependsOn.includes(changedKey)) {
        toInvalidate.add(key);
      }
    }
    
    logger.debug('Invalidating cache keys with dependencies', {
      changedKey,
      toInvalidate: Array.from(toInvalidate),
      userId: userId.substring(0, 8)
    });
    
    // Invalidate all affected keys
    await Promise.allSettled(
      Array.from(toInvalidate).map(key => 
        hybridCache.invalidate(key, userId)
      )
    );
    
    // Queue for background refresh
    toInvalidate.forEach(key => {
      if (this.dependencies.get(key)?.refreshFn) {
        this.refreshQueue.add(key);
        logger.debug('Queued for background refresh', { key });
      }
    });
  }
  
  async preloadDependencies(userId: string): Promise<void> {
    const preloadPromises = Array.from(this.dependencies.entries())
      .filter(([_, dep]) => dep.refreshFn)
      .map(async ([key, dep]) => {
        try {
          if (dep.refreshFn) {
            await hybridCache.preload(key, userId, dep.refreshFn);
          }
        } catch (error) {
          logger.warn('Failed to preload dependency', { key, error });
        }
      });
    
    await Promise.allSettled(preloadPromises);
    logger.debug('Preloaded cache dependencies', { 
      count: preloadPromises.length,
      userId: userId.substring(0, 8)
    });
  }
  
  getDependencyGraph(): Record<string, string[]> {
    const graph: Record<string, string[]> = {};
    for (const [key, dep] of this.dependencies) {
      graph[key] = dep.dependsOn;
    }
    return graph;
  }
  
  private startBackgroundRefresh(): void {
    this.refreshInterval = setInterval(async () => {
      if (this.refreshQueue.size === 0) return;
      
      const keysToRefresh = Array.from(this.refreshQueue);
      this.refreshQueue.clear();
      
      logger.debug('Starting background refresh', { 
        keys: keysToRefresh,
        count: keysToRefresh.length 
      });
      
      await Promise.allSettled(
        keysToRefresh.map(async (key) => {
          const dependency = this.dependencies.get(key);
          if (dependency?.refreshFn) {
            try {
              await dependency.refreshFn();
              logger.debug('Background refresh completed', { key });
              // Cache will be populated by the refresh function
            } catch (error) {
              logger.warn('Background refresh failed', { key, error });
            }
          }
        })
      );
    }, 5000); // Refresh every 5 seconds
  }
  
  destroy(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
    this.dependencies.clear();
    this.refreshQueue.clear();
    logger.debug('Smart cache manager destroyed');
  }
}

// Export singleton instance
export const smartCacheManager = new SmartCacheManager(); 